import dayjs from 'dayjs';
import { normalizeCountryForForm } from '../../data/locationData';
import {
  getDialCodeFromSelectValue,
  resolvePhoneCodeSelectValue,
} from '../../data/phoneCodes';
import { toCalendarYmd } from '../../utils/dateTimeUtils';

const PHONE_CODES = ['+971', '+966', '+965', '+968', '+91', '+86', '+81', '+82', '+65', '+64', '+62', '+49', '+44', '+46', '+45', '+41', '+39', '+34', '+33', '+31', '+1'];

export function parsePhoneFromLead(phone) {
  let phoneCodeValue = '+91';
  let phoneNumber = '';
  if (phone) {
    const phoneStr = String(phone);
    for (const code of PHONE_CODES) {
      if (phoneStr.startsWith(code)) {
        phoneCodeValue = code;
        phoneNumber = phoneStr.substring(code.length).trim();
        break;
      }
    }
    if (!phoneNumber) {
      phoneNumber = phoneStr;
    }
  }
  return { phoneCode: phoneCodeValue, phoneNumber };
}

const toStr = (v) => (v == null || v === '' ? '' : String(v));
const toArr = (v) => (Array.isArray(v) ? v : []);

/** Map API lead → form state + phone code (source of truth for pre-fill). */
export function buildFormStateFromLead(lead) {
  const ymd = toCalendarYmd(lead.date_of_birth);
  const dateOfBirth = ymd ? dayjs(ymd) : null;
  const ageFromDb =
    lead.age != null && lead.age !== '' ? String(lead.age) : '';
  const ageFromDob =
    !ageFromDb && dateOfBirth ? String(dayjs().diff(dateOfBirth, 'year')) : '';
  const { phoneCode: dialCode, phoneNumber } = parsePhoneFromLead(lead.phone);
  const country = normalizeCountryForForm(toStr(lead.country));

  const formData = {
    first_name: toStr(lead.first_name),
    last_name: toStr(lead.last_name),
    email: toStr(lead.email),
    phone: phoneNumber,
    date_of_birth: dateOfBirth,
    age: ageFromDb || ageFromDob,
    class_grade: toStr(lead.class_grade),
    age_group: toStr(lead.age_group),
    student_type: toStr(lead.student_type),
    class_type_preference: toStr(lead.class_type_preference),
    learning_level: toStr(lead.learning_level),
    school_name: toStr(lead.school_name),
    school_address: toStr(lead.school_address),
    school_location: toStr(lead.school_location),
    father_name: toStr(lead.father_name),
    mother_name: toStr(lead.mother_name),
    country,
    state: toStr(lead.state),
    city: toStr(lead.city),
    address: toStr(lead.address),
    timezone: lead.timezone || 'UTC',
    hobby: toStr(lead.hobby),
    parent_fb_ids: toArr(lead.parent_fb_ids),
    parent_linkedin_ids: toArr(lead.parent_linkedin_ids),
    parent_instagram_ids: toArr(lead.parent_instagram_ids),
    source: toStr(lead.source),
    campaign_name: toStr(lead.campaign_name),
    fees: lead.fees != null && lead.fees !== '' ? String(lead.fees) : '',
    discount: lead.discount != null && lead.discount !== '' ? String(lead.discount) : '',
    remarks: toStr(lead.notes),
    lead_acquired_at: lead.lead_acquired_at ? dayjs(lead.lead_acquired_at) : null,
    support_requirements: toStr(lead.support_requirements),
    gender: toStr(lead.gender),
  };

  return {
    formData,
    phoneCode: resolvePhoneCodeSelectValue(dialCode, country),
  };
}

const trimOrNull = (v) => {
  if (v == null) return null;
  const t = String(v).trim();
  return t === '' ? null : t;
};

/** Normalize form state → flat API body (create or diff baseline). */
export function serializeLeadFormPayload(formData, phoneCode) {
  const ageRaw = formData.age;
  let age = null;
  if (ageRaw !== '' && ageRaw != null && !Number.isNaN(Number(ageRaw))) {
    age = parseInt(String(ageRaw), 10);
  }

  let fees = null;
  if (formData.fees !== '' && formData.fees != null && !Number.isNaN(Number(formData.fees))) {
    fees = parseFloat(formData.fees);
  }

  let discount = null;
  if (formData.discount !== '' && formData.discount != null && !Number.isNaN(Number(formData.discount))) {
    discount = parseFloat(formData.discount);
  }

  return {
    first_name: trimOrNull(formData.first_name),
    last_name: trimOrNull(formData.last_name),
    email: trimOrNull(formData.email),
    phone: getDialCodeFromSelectValue(phoneCode) + formData.phone,
    country: trimOrNull(formData.country),
    timezone: formData.timezone || 'UTC',
    state: trimOrNull(formData.state),
    city: trimOrNull(formData.city),
    address: trimOrNull(formData.address),
    date_of_birth: formData.date_of_birth ? formData.date_of_birth.format('YYYY-MM-DD') : null,
    age,
    age_group: trimOrNull(formData.age_group),
    class_grade: trimOrNull(formData.class_grade),
    school_name: trimOrNull(formData.school_name),
    school_address: trimOrNull(formData.school_address),
    school_location: trimOrNull(formData.school_location),
    father_name: trimOrNull(formData.father_name),
    mother_name: trimOrNull(formData.mother_name),
    hobby: trimOrNull(formData.hobby),
    parent_fb_ids: formData.parent_fb_ids || [],
    parent_linkedin_ids: formData.parent_linkedin_ids || [],
    parent_instagram_ids: formData.parent_instagram_ids || [],
    fees,
    discount,
    student_type: trimOrNull(formData.student_type),
    class_type_preference: trimOrNull(formData.class_type_preference),
    learning_level: trimOrNull(formData.learning_level),
    source: trimOrNull(formData.source),
    campaign_name: trimOrNull(formData.campaign_name),
    notes: trimOrNull(formData.remarks),
    lead_acquired_at: formData.lead_acquired_at
      ? formData.lead_acquired_at.format('YYYY-MM-DD')
      : null,
    support_requirements: trimOrNull(formData.support_requirements),
    gender: trimOrNull(formData.gender),
  };
}

function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  if (a == null || b == null) return false;
  if (typeof a === 'number' && typeof b === 'number') {
    return a === b;
  }
  return String(a) === String(b);
}

/** Only keys that changed; null in patch means user cleared a previously set value. */
export function buildLeadUpdatePatch(initial, current) {
  const patch = {};
  Object.keys(current).forEach((key) => {
    if (!valuesEqual(initial[key], current[key])) {
      patch[key] = current[key];
    }
  });
  return patch;
}
